<template>
  <div class="sponsor-dash min-h-screen flex flex-col items-center justify-start px-6 sm:px-8 py-16">

    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center justify-center min-h-[60vh] gap-6">
      <div class="w-16 h-16 rounded-full border-3 border-indigo-500 border-t-transparent animate-spin"></div>
      <p class="text-base text-slate-400">Memuat dashboard sponsor...</p>
    </div>

    <!-- Expired -->
    <div v-else-if="expired" class="flex flex-col items-center justify-center min-h-[60vh] text-center max-w-md gap-8">
      <div class="w-20 h-20 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
        <svg class="w-10 h-10 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <div>
        <h1 class="text-2xl font-extrabold text-slate-100 mb-3">Periode Laporan Berakhir</h1>
        <p class="text-base text-slate-400 leading-relaxed">Link akses dashboard ini sudah tidak valid. Hubungi penyelenggara event untuk informasi lebih lanjut.</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex flex-col items-center justify-center min-h-[60vh] text-center max-w-md gap-8">
      <div class="w-20 h-20 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
        <svg class="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
        </svg>
      </div>
      <div>
        <h1 class="text-2xl font-extrabold text-slate-100 mb-3">Link Tidak Valid</h1>
        <p class="text-base text-slate-400 leading-relaxed">Tidak dapat memuat data dashboard. Pastikan link yang Anda gunakan benar.</p>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else-if="metrics" class="w-full max-w-3xl space-y-8">

      <!-- Header Brand -->
      <div class="sd-card p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <div class="w-16 h-16 rounded-2xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center text-3xl font-extrabold text-indigo-400 flex-shrink-0">
          {{ metrics.brandName?.charAt(0) || 'S' }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-3 mb-2">
            <span class="text-xs font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full">Live</span>
            <span class="text-xs text-slate-500 font-mono">{{ metrics.zone?.replace(/_/g, ' ') }}</span>
          </div>
          <h1 class="text-2xl font-extrabold text-slate-100 truncate">{{ metrics.brandName }}</h1>
          <p class="text-sm text-slate-400 mt-1">Sponsor Performance Dashboard · Merdeka Roll-In 2026</p>
        </div>
        <div class="text-right flex-shrink-0">
          <p class="text-xs text-slate-500 uppercase tracking-wider mb-1">Terakhir update</p>
          <p class="text-sm font-mono text-slate-300 mb-2">{{ lastUpdated }}</p>
          <div class="flex items-center justify-end gap-2">
            <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span class="text-xs text-emerald-400 font-semibold">Auto-refresh 30s</span>
          </div>
        </div>
      </div>

      <!-- Key Metrics Row -->
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-5">
        <div class="sd-card p-6 text-center space-y-2">
          <p class="text-5xl font-extrabold text-indigo-400 font-mono tabular-nums">{{ metrics.totalBoothCheckins }}</p>
          <p class="text-xs text-slate-400 uppercase tracking-wider font-semibold">Total Check-ins</p>
        </div>
        <div class="sd-card p-6 text-center space-y-2">
          <p class="text-5xl font-extrabold text-emerald-400 font-mono tabular-nums">{{ metrics.uniqueParticipants }}</p>
          <p class="text-xs text-slate-400 uppercase tracking-wider font-semibold">Peserta Unik</p>
        </div>
        <div class="col-span-2 sm:col-span-1 sd-card p-6 text-center space-y-2">
          <p class="text-5xl font-extrabold text-amber-400 font-mono tabular-nums">{{ metrics.overallCheckinRate?.toFixed(1) }}%</p>
          <p class="text-xs text-slate-400 uppercase tracking-wider font-semibold">Check-in Rate</p>
        </div>
      </div>

      <!-- Progress vs Total Peserta -->
      <div class="sd-card p-8 space-y-5">
        <div class="flex justify-between items-baseline">
          <h3 class="text-base font-bold text-slate-200">Progres vs Total Peserta Event</h3>
          <span class="text-2xl font-extrabold text-indigo-400 font-mono">{{ progressPercentage }}%</span>
        </div>
        <div class="w-full h-4 rounded-full bg-indigo-500/10 overflow-hidden">
          <div
            class="h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all duration-700"
            :style="{ width: `${progressPercentage}%` }"
          ></div>
        </div>
        <p class="text-sm text-slate-400 text-center">
          <span class="font-bold text-slate-200">{{ metrics.uniqueParticipants }}</span> dari
          <span class="font-bold text-slate-200">{{ metrics.eventTotalAttendees }}</span> peserta hadir di booth Anda
        </p>
      </div>

      <!-- Demografi Usia -->
      <div v-if="metrics.ageDistribution?.length" class="sd-card p-8 space-y-5">
        <h3 class="text-base font-bold text-slate-200">Distribusi Usia</h3>
        <div class="space-y-4">
          <div v-for="age in metrics.ageDistribution" :key="age.bucket" class="space-y-2">
            <div class="flex justify-between text-sm">
              <span class="text-slate-300 font-medium">{{ age.bucket }}</span>
              <span class="text-slate-400 font-mono">{{ age.count }} ({{ age.percentage.toFixed(1) }}%)</span>
            </div>
            <div class="w-full h-3 rounded-full bg-indigo-500/10 overflow-hidden">
              <div
                class="h-full rounded-full bg-indigo-500/60 transition-all duration-700"
                :style="{ width: `${age.percentage}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Domisili -->
      <div v-if="metrics.domicileDistribution?.length" class="sd-card p-8 space-y-5">
        <h3 class="text-base font-bold text-slate-200">Top Domisili</h3>
        <div class="space-y-4">
          <div v-for="(dom, i) in metrics.domicileDistribution.slice(0, 5)" :key="dom.city" class="flex items-center gap-4">
            <span class="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 text-xs font-bold text-slate-400 flex items-center justify-center flex-shrink-0">{{ i + 1 }}</span>
            <div class="flex-1 min-w-0">
              <div class="flex justify-between text-sm mb-2">
                <span class="text-slate-300 font-medium truncate">{{ dom.city }}</span>
                <span class="text-slate-400 font-mono flex-shrink-0 ml-3">{{ dom.percentage.toFixed(1) }}%</span>
              </div>
              <div class="w-full h-2 rounded-full bg-emerald-500/10 overflow-hidden">
                <div class="h-full rounded-full bg-emerald-500/50 transition-all duration-700" :style="{ width: `${dom.percentage}%` }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Distribusi Jenis Motor (jika ada) -->
      <div v-if="metrics.motorcycleTypeDistribution?.length" class="sd-card p-8 space-y-5">
        <h3 class="text-base font-bold text-slate-200">Jenis Motor Pengunjung</h3>
        <div class="grid grid-cols-2 gap-4">
          <div v-for="moto in metrics.motorcycleTypeDistribution" :key="moto.type" class="bg-slate-950/50 border border-slate-800/60 rounded-xl p-5 text-center space-y-2">
            <p class="text-2xl font-extrabold text-amber-400 font-mono">{{ moto.percentage.toFixed(0) }}%</p>
            <p class="text-xs text-slate-400 font-semibold uppercase tracking-wider">{{ moto.type }}</p>
            <p class="text-xs text-slate-600 font-mono">{{ moto.count }} peserta</p>
          </div>
        </div>
      </div>

      <!-- Privacy Notice -->
      <div class="sd-card p-7 flex gap-4 items-start">
        <div class="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg class="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-bold text-slate-300 mb-2">Data Privasi Terlindungi</p>
          <p class="text-sm text-slate-500 leading-relaxed">
            Dashboard ini hanya menampilkan data agregat. Informasi pribadi peserta (nama, nomor WhatsApp, email) tidak ditampilkan sesuai kebijakan privasi Rameria.
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="text-center py-6 space-y-2">
        <p class="text-sm font-bold text-slate-400">Powered by <span class="text-indigo-400">Rameria</span></p>
        <p class="text-xs text-slate-600">Laporan lengkap akan dikirimkan H+5 pasca acara</p>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const API_BASE = '/api'

const loading = ref(true)
const expired = ref(false)
const error = ref(false)
const metrics = ref<any>(null)
const lastUpdated = ref('')

let pollingInterval: any = null

const progressPercentage = computed(() => {
  if (!metrics.value?.eventTotalAttendees) return 0
  return Math.min(100, Math.round((metrics.value.uniqueParticipants / metrics.value.eventTotalAttendees) * 100))
})

async function fetchMetrics() {
  const token = route.params.token as string
  if (!token) { error.value = true; loading.value = false; return }

  try {
    const res = await fetch(`${API_BASE}/sponsor-view/${token}/metrics`)
    if (res.status === 410) { expired.value = true; loading.value = false; return }
    if (!res.ok) { error.value = true; loading.value = false; return }
    metrics.value = await res.json()
    lastUpdated.value = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    loading.value = false
  } catch {
    error.value = true
    loading.value = false
  }
}

onMounted(() => {
  fetchMetrics()
  pollingInterval = setInterval(fetchMetrics, 30000)
})
onUnmounted(() => { if (pollingInterval) clearInterval(pollingInterval) })
</script>

<style scoped>
.sponsor-dash {
  background-color: #05070f;
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.015) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.015) 1px, transparent 1px);
  background-size: 32px 32px;
  color: #f8fafc;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}

.sd-card {
  background-color: rgba(11, 15, 29, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 1.25rem;
  backdrop-filter: blur(16px);
}
</style>
